import React, { Fragment, useState } from "react";
import moment from 'moment';

import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Avatar,
    Stack,
    IconButton
} from '@mui/material';

import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CoffeeIcon from '@mui/icons-material/Coffee';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PhonelinkOffIcon from '@mui/icons-material/PhonelinkOff';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';

// warnings проп соответствует структуре из WARNINGS_API.md,
// но компонент остаётся совместимым с более старыми форматами.
const Messages = ({ warnings = {} }) => {
    // Состояние для фильтрации по типам предупреждений (чекбоксы)
    const [filterSeverity, setFilterSeverity] = useState({
        info: true,
        warning: true,
        critical: true
    });
    const {
        restOverLimit = null,
        notReadyOverTime = [],
        statusChangedToWork = [],
        operatorJoined = [],
        operatorDisconnected = [],
        events = []
    } = warnings || {};

    // Нормализуем массив операторов «не готов» (поддержка старого формата с .agents)
    const getNotReadyArray = () => {
        if (Array.isArray(notReadyOverTime)) return notReadyOverTime;
        if (notReadyOverTime && Array.isArray(notReadyOverTime.agents)) {
            return notReadyOverTime.agents;
        }
        return [];
    };
    
    // Определяем категорию (severity) для каждого типа события
    const getSeverity = (type) => {
        switch (type) {
            case 'restOverLimit':
                return 'warning'; // Предупреждение
            case 'notReadyOverTime':
                return 'critical'; // Критическое
            case 'statusChangedToWork':
            case 'operatorJoined':
            case 'operatorDisconnected':
                return 'info'; // Информационный
            default:
                return 'info';
        }
    };

    // Берём events, если сервер их присылает,
    // иначе собираем события из отдельных массивов
    const getEventsArray = () => {
        if (Array.isArray(events) && events.length > 0) {
            return events;
        }

        const result = [];

        if (Array.isArray(statusChangedToWork)) {
            statusChangedToWork.forEach(e => result.push({ ...e, type: 'statusChangedToWork' }));
        }
        if (Array.isArray(operatorJoined)) {
            operatorJoined.forEach(e => result.push({ ...e, type: 'operatorJoined' }));
        }
        if (Array.isArray(operatorDisconnected)) {
            operatorDisconnected.forEach(e => result.push({ ...e, type: 'operatorDisconnected' }));
        }

        return result;
    };

    // Объединяем все уведомления
    const allNotifications = [];

    // 1. restOverLimit — агрегированное предупреждение по перерывам
    if (restOverLimit && restOverLimit.count > 0) {
        allNotifications.push({
            id: 'restOverLimit',
            type: 'restOverLimit', 
            time: restOverLimit.time,
            count: restOverLimit.count,
            limit: restOverLimit.limit,
            agents: Array.isArray(restOverLimit.agents) ? restOverLimit.agents : [],
            severity: 'warning',
            timestamp: restOverLimit.timestamp
        });
    }

    // 2. notReadyOverTime — операторы «не готов» > 2 минут
    const notReadyArray = getNotReadyArray();
    notReadyArray.forEach(agent => {
        allNotifications.push({
            ...agent,
            type: 'notReadyOverTime', 
            severity: 'critical'
        });
    });

    // 3. events — общая лента событий операторов (накопительная история)
    const eventsArray = getEventsArray();
    eventsArray.forEach(event => {
        allNotifications.push({
            ...event,
            type: event.type || 'event',
            severity: getSeverity(event.type || 'event')
        });
    });

    // Сортируем по времени по убыванию: новые сообщения наверху
    allNotifications.sort((a, b) => {
        const getMoment = (item) => {
            if (item.timestamp) {
                return moment(item.timestamp);
            }
            if (item.time) {
                // time может быть в формате "HH:MM" или "HH:MM:SS"
                const timeParts = item.time.split(':').map(Number);
                const now = moment();
                const momentTime = moment(now)
                    .hours(timeParts[0] || 0)
                    .minutes(timeParts[1] || 0)
                    .seconds(timeParts[2] || 0)
                    .milliseconds(0);
                
                // Если время больше текущего времени, значит это было вчера
                if (momentTime.isAfter(now)) {
                    return momentTime.subtract(1, 'day');
                }
                return momentTime;
            }
            return moment(0);
        };

        return getMoment(b).diff(getMoment(a));
    });

    // Фильтруем уведомления по выбранным типам (чекбоксы)
    const filteredNotifications = allNotifications.filter(item => {
        const severity = item.severity || getSeverity(item.type);
        return filterSeverity[severity] === true;
    });

    const totalWarnings = filteredNotifications.length;
    
    const formatTime = (item) => {
        if (item.stateDuration) return item.stateDuration;
        if (item.duration) return item.duration;
        if (item.durationSeconds) {
            const minutes = Math.floor(item.durationSeconds / 60);
            const seconds = item.durationSeconds % 60;
            return `${minutes}:${String(seconds).padStart(2, '0')}`;
        }
        if (item.time) return item.time;
        return '';
    };

    // Форматирование расписания смены
    const formatSchedule = (schedule) => {
        // Проверяем наличие schedule и его полей
        if (!schedule || typeof schedule !== 'object') {
            return null;
        }
        
        // Проверяем, что start и end существуют и не null
        const startValue = schedule.start;
        const endValue = schedule.end;
        
        // Используем == для проверки на null и undefined одновременно
        if (startValue == null || endValue == null || startValue === '' || endValue === '') {
            return null; // Смена отсутствует
        }
        
        try {
            // Парсим даты через moment (поддерживает ISO строки, Date объекты и другие форматы)
            const start = moment(startValue);
            const end = moment(endValue);
            
            // Проверяем валидность дат
            if (!start.isValid() || !end.isValid()) {
                console.warn('Invalid schedule dates:', { 
                    start: startValue, 
                    end: endValue, 
                    startValid: start.isValid(),
                    endValid: end.isValid(),
                    schedule 
                });
                return null; // Некорректные даты
            }
            
            // Форматируем время начала и конца смены
            return `${start.format('HH:mm')} - ${end.format('HH:mm')}`;
        } catch (error) {
            console.error('Error formatting schedule:', error, { schedule, startValue, endValue });
            return null;
        }
    };
    
    const getNotificationConfig = (type) => {
        switch (type) {
            case 'restOverLimit':
            return {
                icon: <CoffeeIcon />,
                iconColor: '#FF9800',
                iconBg: '#FFF3E0',
                    title: 'Превышение лимита перерывов',
                    color: 'warning',
                    severity: 'warning'
            };
            case 'notReadyOverTime':
        return {
            icon: <AccessTimeIcon />,
            iconColor: '#F44336',
            iconBg: '#FFEBEE',
                    title: 'Оператор не готов более 2 минут',
                    color: 'error',
                    severity: 'critical'
                };
            case 'statusChangedToWork':
                return {
                    icon: <EngineeringIcon />,
                    iconColor: '#3f51b5',
                    iconBg: '#E8EAF6',
                    title: 'Оператор установил статус "Работаю"',
                    color: 'info',
                    severity: 'info'
                };
            case 'operatorJoined':
                return {
                    icon: <PersonAddAlt1Icon />,
                    iconColor: '#4CAF50',
                    iconBg: '#E8F5E9',
                    title: 'Новый оператор в смене',
                    color: 'info',
                    severity: 'info'
                };
            case 'operatorDisconnected':
                return {
                    icon: <PhonelinkOffIcon />,
                    iconColor: '#9E9E9E',
                    iconBg: '#F5F5F5',
                    title: 'Оператор отключился',
                    color: 'default',
                    severity: 'info'
                };
            default:
                return {
                    icon: <AccessTimeIcon />,
                    iconColor: '#757575',
                    iconBg: '#EEEEEE',
                    title: 'Событие',
                    color: 'default',
                    severity: 'info'
                };
        }
    };

    return(
        <Fragment>
            <Card variant="outlined">
                <CardContent>
                    <div className="pc-dash-events-head">
                        <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        События
                    </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                                onClick={() => setFilterSeverity(prev => ({ ...prev, info: !prev.info }))}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: filterSeverity.info ? '#2196F3' : '#BDBDBD',
                                    backgroundColor: filterSeverity.info ? '#E3F2FD' : 'transparent',
                                    border: `1px solid ${filterSeverity.info ? '#2196F3' : '#E0E0E0'}`,
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: filterSeverity.info ? '#BBDEFB' : '#F5F5F5',
                                        borderColor: '#2196F3'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <InfoIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                            <IconButton
                                onClick={() => setFilterSeverity(prev => ({ ...prev, warning: !prev.warning }))}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: filterSeverity.warning ? '#FF9800' : '#BDBDBD',
                                    backgroundColor: filterSeverity.warning ? '#FFF3E0' : 'transparent',
                                    border: `1px solid ${filterSeverity.warning ? '#FF9800' : '#E0E0E0'}`,
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: filterSeverity.warning ? '#FFE0B2' : '#F5F5F5',
                                        borderColor: '#FF9800'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <WarningIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                            <IconButton
                                onClick={() => setFilterSeverity(prev => ({ ...prev, critical: !prev.critical }))}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: filterSeverity.critical ? '#F44336' : '#BDBDBD',
                                    backgroundColor: filterSeverity.critical ? '#FFEBEE' : 'transparent',
                                    border: `1px solid ${filterSeverity.critical ? '#F44336' : '#E0E0E0'}`,
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: filterSeverity.critical ? '#FFCDD2' : '#F5F5F5',
                                        borderColor: '#F44336'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <ErrorIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                        </Box>
                    </div>
                    
                    {totalWarnings > 0 ? (
                        <div className="pc-dash-events">
                            <Stack spacing={0.5}>
                                {filteredNotifications.map((item, index) => {
                                    const config = getNotificationConfig(item.type);
                                    const timeDisplay = formatTime(item);
                                    const agentName = `${item.name || item.login || 'Неизвестно'}`;
                                    const key = `${item.type}-${item.agentId || item.id || 'unknown'}-${item.timestamp || item.time || index}-${index}`;
                                    
                                    let timeAgo = '';
                                    if (item.timestamp) {
                                        const timestamp = moment(item.timestamp);
                                        const diffInSeconds = moment().diff(timestamp, 'seconds');
                                        
                                        if (diffInSeconds < 60) {
                                            timeAgo = timestamp.fromNow();
                                        } else {
                                            timeAgo = timestamp.format('HH:mm');
                                        }
                                    }
                                    
                                    return (
                                        <div className="pc-dash-event" key={key}>
                                            <Avatar
                                                sx={{
                                                    width: 22,
                                                    height: 22,
                                                    backgroundColor: config.iconBg,
                                                    color: config.iconColor,
                                                    marginRight: 1,
                                                    fontSize: '0.7rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {config.icon}
                                            </Avatar>
                                            
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {item.type === 'restOverLimit' ? (
                                                    <>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 600,
                                                                marginBottom: 0.25,
                                                                fontSize: '0.72rem',
                                                                lineHeight: 1.25,
                                                                color: '#212121'
                                                            }}
                                                        >
                                                            {config.title}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                fontSize: '0.62rem',
                                                                display: 'block',
                                                                marginBottom: 0.25
                                                            }}
                                                        >
                                                            {`Операторов в перерыве: ${item.count} (лимит: ${item.limit})`}
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
                                                            {(item.agents || []).map((agent, agentIndex) => (
                                                                <Typography
                                                                    key={`${item.id || 'restOverLimit'}-agent-${agent.id || agent.login || agentIndex}-${agentIndex}`}
                                                                    variant="caption"
                                                                    sx={{ 
                                                                        color: '#424242',
                                                                        fontSize: '0.62rem'
                                                                    }}
                                                                >
                                                                    {(agent.login || '')} {agent.name || ''} — {agent.stateDuration || agent.duration || ''}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        marginBottom: 0,
                                                        fontSize: '0.7rem',
                                                        lineHeight: 1.2,
                                                        color: '#212121'
                                                    }}
                                                >
                                                    {agentName}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: 'text.secondary',
                                                        fontSize: '0.62rem',
                                                        display: 'block',
                                                        lineHeight: 1.25
                                                    }}
                                                >
                                                    {config.title}
                                                </Typography>
                                                {(item.type === 'operatorJoined' || item.type === 'operatorDisconnected') && item.station && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.62rem',
                                                            display: 'block'
                                                        }}
                                                    >
                                                        Рабочее место: {item.station}
                                                    </Typography>
                                                )}
                                                
                                                {(item.type === 'operatorJoined' || item.type === 'operatorDisconnected') && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: formatSchedule(item.schedule) ? 'text.secondary' : '#F44336',
                                                            fontSize: '0.62rem',
                                                            display: 'block',
                                                            fontWeight: formatSchedule(item.schedule) ? 'normal' : 500
                                                        }}
                                                    >
                                                        {formatSchedule(item.schedule) ? `Смена: ${formatSchedule(item.schedule)}` : 'Смена отсутствует'}
                                                    </Typography>
                                                )}
                                                    </>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                                                    {timeDisplay && (
                                                        <Chip
                                                            size="small"
                                                            label={`Время: ${timeDisplay}`}
                                                            sx={{
                                                                height: 16,
                                                                fontSize: '0.62rem',
                                                                backgroundColor: config.iconBg,
                                                                color: config.iconColor,
                                                                fontWeight: 500,
                                                                padding: '0 2px'
                                                            }}
                                                        />
                                                    )}
                                                    {timeAgo && (
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                fontSize: '0.62rem'
                                                            }}
                                                        >
                                                            {timeAgo}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </div>
                                    );
                                })}
                            </Stack>
                        </div>
                    ) : (
                        <Box sx={{ 
                            flex: 1,
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            padding: 2,
                            color: 'text.secondary',
                            minHeight: 0
                        }}>
                            <WarningIcon sx={{ fontSize: '1.5rem', marginBottom: 0.5, opacity: 0.3 }} />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                Нет активных предупреждений
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Fragment>
    )
}

export default Messages;
